import './WorkOrderPhotos.scss';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Button from 'devextreme-react/button';
import LoadIndicator from 'devextreme-react/load-indicator';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import notify from 'devextreme/ui/notify';
import { useAuth } from '../../../contexts/auth';
import {
  WorkOrderPhotoDto,
  getWorkOrderPhotos,
  uploadWorkOrderPhotos,
  deleteWorkOrderPhoto,
  getPhotoUrl,
} from '../../../api/work-order-photos';

// ─── Authenticated image component ─────────────────────────────────────────
// <img> can't send Bearer headers – fetch the blob with axios and use an objectURL
interface AuthImgProps {
  src: string;
  token: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}
const AuthImg = ({ src, token, alt, className, style, onClick }: AuthImgProps) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    fetch(src, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        if (!revoked) setBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => { /* silently fail – broken image is fine */ });
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src, token]);

  if (!blobUrl) return <div className='photo-placeholder' />;
  return <img src={blobUrl} alt={alt} className={className} style={style} onClick={onClick} />;
};
// ────────────────────────────────────────────────────────────────────────────

interface WorkOrderPhotosProps {
  workOrderOid: string;
}

export const WorkOrderPhotos = ({ workOrderOid }: WorkOrderPhotosProps) => {
  const { user } = useAuth();
  const [photos, setPhotos] = useState<WorkOrderPhotoDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState<WorkOrderPhotoDto | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadPhotos = useCallback(async() => {
    if (!user?.token || !workOrderOid) return;
    setLoading(true);
    try {
      const data = await getWorkOrderPhotos(user.token, workOrderOid);
      setPhotos(data);
    } catch {
      notify('Foto\'s laden mislukt.', 'error', 3000);
    } finally {
      setLoading(false);
    }
  }, [user?.token, workOrderOid]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const onUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFilesSelected = useCallback(async(e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!user?.token) return;

    const allowedTypes = ['image/jpeg', 'image/png'];
    const maxSize = 10 * 1024 * 1024;
    for (const f of files) {
      if (!allowedTypes.includes(f.type)) {
        notify(`Bestand '${f.name}' is geen geldig type (jpg/png).`, 'error', 4000);
        return;
      }
      if (f.size > maxSize) {
        notify(`Bestand '${f.name}' is groter dan 10 MB.`, 'error', 4000);
        return;
      }
    }

    setUploading(true);
    try {
      await uploadWorkOrderPhotos(user.token, workOrderOid, files);
      await loadPhotos();
      notify(`${files.length} foto(s) geüpload.`, 'success', 3000);
    } catch {
      notify('Upload mislukt.', 'error', 3000);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [user?.token, workOrderOid, loadPhotos]);

  const onDeletePhoto = useCallback(async(photo: WorkOrderPhotoDto) => {
    if (!user?.token) return;
    try {
      await deleteWorkOrderPhoto(user.token, workOrderOid, photo.Oid);
      setPhotos((prev) => prev.filter((p) => p.Oid !== photo.Oid));
      if (lightboxPhoto?.Oid === photo.Oid) setLightboxPhoto(null);
      notify('Foto verwijderd.', 'success', 2000);
    } catch {
      notify('Verwijderen mislukt.', 'error', 3000);
    }
  }, [user?.token, workOrderOid, lightboxPhoto]);

  const closeLightboxOptions = { text: 'Sluiten', onClick: () => setLightboxPhoto(null) };

  return (
    <div className='work-order-photos'>
      <div className='photos-toolbar'>
        <Button
          text={uploading ? 'Bezig...' : 'Foto\'s uploaden'}
          icon='upload'
          type='default'
          disabled={uploading}
          onClick={onUploadClick}
        />
        <input
          ref={fileInputRef}
          type='file'
          accept='image/jpeg,image/png'
          multiple
          style={{ display: 'none' }}
          onChange={onFilesSelected}
        />
      </div>

      {loading ? (
        <div className='photos-loading'>
          <LoadIndicator visible />
        </div>
      ) : photos.length === 0 ? (
        <div className='photos-empty'>
          <i className='dx-icon-image photos-empty-icon' />
          <p>Geen foto&apos;s beschikbaar</p>
        </div>
      ) : (
        <div className='photos-grid'>
          {photos.map((photo) => (
            <div key={photo.Oid} className='photo-tile'>
              <AuthImg
                src={getPhotoUrl(workOrderOid, photo.Oid)}
                token={user?.token ?? ''}
                alt={photo.FileName}
                onClick={() => setLightboxPhoto(photo)}
              />
              <div className='photo-overlay'>
                <span className='photo-name'>{photo.FileName}</span>
                <Button
                  icon='trash'
                  stylingMode='text'
                  type='danger'
                  hint='Verwijderen'
                  onClick={(e) => { e.event?.stopPropagation(); onDeletePhoto(photo); }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <Popup
        visible={!!lightboxPhoto}
        onHiding={() => setLightboxPhoto(null)}
        dragEnabled={false}
        hideOnOutsideClick
        showTitle
        title={lightboxPhoto?.FileName ?? ''}
        width='90vw'
        height='90vh'
        maxWidth={1200}
      >
        <ToolbarItem widget='dxButton' toolbar='bottom' location='center' options={closeLightboxOptions} />
        {lightboxPhoto && (
          <AuthImg
            src={getPhotoUrl(workOrderOid, lightboxPhoto.Oid)}
            token={user?.token ?? ''}
            alt={lightboxPhoto.FileName}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', margin: '0 auto' }}
          />
        )}
      </Popup>
    </div>
  );
};
